import { PerformanceCard } from '@/components/shared/PerformanceCard';
import User from '@/models/User';
import { useHourSaudation } from '@/utils/useHourSaudation';
import { Trans, useTranslation } from 'react-i18next';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton } from '@/components/ui/daisy';
import normalize from '@/utils/statistics/normalize';
import { PendenciesCount } from '@/components/shared/PendenciesCount';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useStatisticService from '@/data/hook/useStatistics';
import { studentsPerformanceQueries } from '@/data/query-keys/studentsPerformanceQueryKey';

type TeacherResumeProps = {
  user: User;
};

export function TeacherResume({ user }: TeacherResumeProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const { t: studentPerformanceT } = useTranslation('translation', {
    keyPrefix: 'dashboard.studentsPerformance',
  });

  const { statistic: studentPerformance, isInitialLoading: isLoading } =
    useStatisticService({
      ...studentsPerformanceQueries.list({}),
      staleTime: REQUEST_STALE_TIME_IN_MS,
      keepPreviousData: true,
    });

  const performance = studentPerformance?.performance
    ? normalize(studentPerformance?.performance * 100, 0)
    : 0;
  const studentsCount = studentPerformance?.studentsCount || 0;
  const radialProgressChildren = `${performance}%`;

  return (
    <ConditionalRenderer
      condition={!isLoading}
      fallback={<TeacherResumeSkeleton />}
    >
      <div className="flex flex-col gap-6 lg:h-full lg:overflow-hidden">
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-primary">
            {useHourSaudation()},
          </h2>
          <p className="text-2xl font-medium">
            {t('teacherPrefix')} {user.firstName}
          </p>
        </div>
        <PerformanceCard
          performance={performance}
          title={studentPerformanceT('title')}
          description={
            <Trans
              i18nKey="dashboard.studentsPerformance.description"
              values={{ studentsCount }}
              components={{ bold: <strong className="font-semibold" /> }}
            />
          }
          radialProgressChildren={radialProgressChildren}
        />
        <PendenciesCount />
      </div>
    </ConditionalRenderer>
  );
}

const TeacherResumeSkeleton = () => {
  return (
    <div className="flex flex-col gap-6 rounded-2xl bg-base-100 lg:h-full lg:overflow-hidden">
      <div className="flex flex-col gap-2">
        <Skeleton className="w-24 h-4" />
        <Skeleton className="w-48 h-6" />
      </div>
      <Skeleton className="w-full h-36" />
      <Skeleton className="w-full h-36" />
    </div>
  );
};
